import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Flex,
  Stack,
  Text,
  Spinner,
  Switch,
  HStack,
  IconButton,
  Input,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon, CheckIcon, CloseIcon } from "@chakra-ui/icons";

import {
  JOB_CRITERIA_QUERY,
  ADD_JOB_CRITERION_MUTATION,
  UPDATE_JOB_CRITERION_MUTATION,
  REMOVE_JOB_CRITERION_MUTATION,
} from "../graphql/jobs";

function singularize(name) {
  if (!name) return name;
  const lower = name.toLowerCase();
  if (lower.endsWith("ies")) {
    const replacement = name.slice(-3) === "IES" ? "Y" : "y";
    return name.slice(0, -3) + replacement;
  }
  if (lower.endsWith("s")) return name.slice(0, -1);
  return name;
}

// Canonical category sections always shown in the editor, in this order, so
// users can add a criterion to any category even when extraction returned none.
// Slugs must match those used by the backend so existing criteria merge in.
const DEFAULT_CATEGORY_TEMPLATES = [
  { slug: "location", name: "Locations" },
  { slug: "right_to_work", name: "Right to work" },
  { slug: "certification", name: "Certifications" },
  { slug: "experience", name: "Experience" },
  { slug: "other", name: "Other" },
];

export function mergeCategoriesWithDefaults(backendCategories) {
  const bySlug = new Map(
    (backendCategories || []).map((c) => [c.slug, c])
  );
  const merged = DEFAULT_CATEGORY_TEMPLATES.map((template) => {
    const fromBackend = bySlug.get(template.slug);
    if (fromBackend) {
      return { ...template, ...fromBackend, criteria: fromBackend.criteria || [] };
    }
    return { ...template, criteria: [] };
  });
  const templateSlugs = new Set(DEFAULT_CATEGORY_TEMPLATES.map((t) => t.slug));
  for (const cat of backendCategories || []) {
    if (!templateSlugs.has(cat.slug)) {
      merged.push({ ...cat, criteria: cat.criteria || [] });
    }
  }
  return merged;
}

export function RequirementItem({ requirement, categorySlug, onUpdate, onRemove }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(requirement.title);
  const [editDescription, setEditDescription] = useState(requirement.description);

  const handleSave = () => {
    onUpdate(requirement.id, {
      categorySlug,
      title: editTitle,
      description: editDescription,
      isRequired: requirement.isRequired,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditTitle(requirement.title);
    setEditDescription(requirement.description);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Stack
        p="12px"
        bg="gray.800"
        borderRadius="8px"
        gap="8px"
      >
        <Input
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          size="sm"
          bg="gray.700"
          border="none"
          color="white"
        />
        <Textarea
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
          size="sm"
          bg="gray.700"
          border="none"
          color="white"
          rows={2}
        />
        <HStack justify="flex-end" spacing={2}>
          <IconButton
            icon={<CheckIcon />}
            size="sm"
            colorScheme="green"
            onClick={handleSave}
            aria-label="Save"
          />
          <IconButton
            icon={<CloseIcon />}
            size="sm"
            variant="ghost"
            color="white"
            onClick={handleCancel}
            aria-label="Cancel"
          />
        </HStack>
      </Stack>
    );
  }

  return (
    <HStack
      p="12px"
      bg="gray.800"
      borderRadius="8px"
      justify="space-between"
      align="flex-start"
    >
      <Stack gap="2px" flex="1">
        <Text fontSize="sm" fontWeight="semibold" color="white">
          {requirement.title}
        </Text>
        {requirement.description && (
          <Text fontSize="xs" color="whiteAlpha.700">
            {requirement.description}
          </Text>
        )}
      </Stack>
      <HStack spacing={2}>
        <Switch
          size="sm"
          colorScheme="teal"
          isChecked={requirement.isRequired}
          aria-label="Must match"
          title={requirement.isRequired ? "Must match" : "Optional"}
          onChange={() => {
            onUpdate(requirement.id, {
              categorySlug,
              title: requirement.title,
              description: requirement.description,
              isRequired: !requirement.isRequired,
            });
          }}
        />
        <IconButton
          icon={<EditIcon />}
          size="sm"
          variant="ghost"
          color="whiteAlpha.700"
          _hover={{ color: "white" }}
          onClick={() => setIsEditing(true)}
          aria-label="Edit requirement"
        />
        <IconButton
          icon={<DeleteIcon />}
          size="sm"
          variant="ghost"
          color="red.400"
          _hover={{ color: "red.300" }}
          onClick={() => onRemove(requirement)}
          aria-label="Delete requirement"
        />
      </HStack>
    </HStack>
  );
}

export default function JobRequirementsEditor({ moblyzeJobId, pollWhileProcessing = false }) {
  const toast = useToast();
  const [addingSlug, setAddingSlug] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [criterionToDelete, setCriterionToDelete] = useState(null);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const { data, loading, error, startPolling, stopPolling } = useQuery(
    JOB_CRITERIA_QUERY,
    {
      variables: { id: moblyzeJobId },
      fetchPolicy: "cache-and-network",
    }
  );

  const [addCriterion, { loading: addLoading }] = useMutation(ADD_JOB_CRITERION_MUTATION);
  const [updateCriterion, { loading: updateLoading }] = useMutation(UPDATE_JOB_CRITERION_MUTATION);
  const [removeCriterion, { loading: removeLoading }] = useMutation(REMOVE_JOB_CRITERION_MUTATION);
  const isMutating = addLoading || updateLoading || removeLoading;

  const extractionStatus = data?.moblyzeJob?.criteriaExtractionStatus;

  React.useEffect(() => {
    if (!pollWhileProcessing) return undefined;
    if (!extractionStatus || extractionStatus === "Started") {
      startPolling(3000);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [extractionStatus, startPolling, stopPolling, pollWhileProcessing]);

  const handleUpdate = async (criterionId, input) => {
    try {
      await updateCriterion({
        variables: { moblyzeJobId, criterionId, input },
        refetchQueries: [{ query: JOB_CRITERIA_QUERY, variables: { id: moblyzeJobId } }],
      });
    } catch (e) {
      toast({ title: "Error updating requirement", description: e.message, status: "error" });
    }
  };

  const handleRemove = (criterion) => {
    setCriterionToDelete(criterion);
    onDeleteOpen();
  };

  const handleConfirmDelete = async () => {
    if (!criterionToDelete) return;
    const criterion = criterionToDelete;
    onDeleteClose();
    try {
      await removeCriterion({
        variables: { moblyzeJobId, criterionId: criterion.id },
        refetchQueries: [{ query: JOB_CRITERIA_QUERY, variables: { id: moblyzeJobId } }],
      });
    } catch (e) {
      toast({
        title: "Error removing requirement",
        description: e.message,
        status: "error",
      });
    }
  };

  const handleAdd = async (categorySlug) => {
    if (!newTitle.trim()) return;
    try {
      await addCriterion({
        variables: {
          moblyzeJobId,
          input: {
            categorySlug,
            title: newTitle.trim(),
            description: newDescription.trim(),
            isRequired: true,
          },
        },
        refetchQueries: [{ query: JOB_CRITERIA_QUERY, variables: { id: moblyzeJobId } }],
      });
      setAddingSlug(null);
      setNewTitle("");
      setNewDescription("");
    } catch (e) {
      toast({ title: "Error adding requirement", description: e.message, status: "error" });
    }
  };

  const handleCancelAdd = () => {
    setAddingSlug(null);
    setNewTitle("");
    setNewDescription("");
  };

  if (loading && !data) {
    return (
      <Flex align="center" justify="center" minH="200px">
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (error) {
    return <Text color="red.500">Failed to load job requirements.</Text>;
  }

  const isProcessing = !extractionStatus || extractionStatus === "Started";
  const isFailed = extractionStatus === "Failed";
  const criteriaCategories = mergeCategoriesWithDefaults(
    data?.moblyzeJob?.criteriaCategories
  );

  if (isProcessing) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        minH="300px"
        gap="16px"
      >
        <Spinner size="xl" thickness="4px" color="white" />
        <Text fontSize="lg" color="whiteAlpha.800">
          Analyzing job description and extracting requirements...
        </Text>
        <Text fontSize="sm" color="whiteAlpha.600">
          This may take a moment.
        </Text>
      </Flex>
    );
  }

  if (isFailed) {
    return (
      <Text fontSize="lg" color="red.400">
        Failed to process job requirements.
      </Text>
    );
  }

  return (
    <Stack gap="24px">
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered variant="darkMode" size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete requirement</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete &apos;{criterionToDelete?.title}&apos;?
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button colorScheme="red" onClick={handleConfirmDelete}>Delete</Button>
              <Button variant="outline" onClick={onDeleteClose}>Cancel</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <HStack px="12px" align="center">
        <Flex flex="1" align="center">
          {isMutating && <Spinner size="sm" color="teal.300" />}
        </Flex>
        <HStack spacing={2} align="center">
          <Text
            fontSize="sm"
            color="white"
            textTransform="uppercase"
            fontWeight="bold"
            letterSpacing="wider"
          >
            Must match
          </Text>
          <Box w="32px" />
          <Box w="32px" />
        </HStack>
      </HStack>
      {criteriaCategories.map((category) => (
        <Stack key={category.slug} gap="12px">
          <HStack justify="space-between" align="center">
            <Text
              fontSize="md"
              fontWeight="bold"
              color="white"
              textTransform="uppercase"
            >
              {category.name}
            </Text>
            <Button
              size="xs"
              variant="ghost"
              color="teal.200"
              leftIcon={<AddIcon boxSize="10px" />}
              _hover={{ color: "teal.100", bg: "whiteAlpha.100" }}
              onClick={() => {
                setAddingSlug(category.slug);
                setNewTitle("");
                setNewDescription("");
              }}
              isDisabled={addingSlug === category.slug}
            >
              {singularize(category.name)}
            </Button>
          </HStack>
          <Stack gap="8px">
            {category.criteria.map((req) => (
              <RequirementItem
                key={req.id}
                requirement={req}
                categorySlug={category.slug}
                onUpdate={handleUpdate}
                onRemove={handleRemove}
              />
            ))}
            {addingSlug === category.slug && (
              <Stack
                p="12px"
                bg="gray.800"
                borderRadius="8px"
                gap="8px"
              >
                <Input
                  autoFocus
                  placeholder={`New ${singularize(category.name)}`}
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  size="sm"
                  bg="gray.700"
                  border="none"
                  color="white"
                />
                <Textarea
                  placeholder="Description (optional)"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  size="sm"
                  bg="gray.700"
                  border="none"
                  color="white"
                  rows={2}
                />
                <HStack justify="flex-end" spacing={2}>
                  <IconButton
                    icon={<CheckIcon />}
                    size="sm"
                    colorScheme="green"
                    isDisabled={!newTitle.trim()}
                    onClick={() => handleAdd(category.slug)}
                    aria-label="Add requirement"
                  />
                  <IconButton
                    icon={<CloseIcon />}
                    size="sm"
                    variant="ghost"
                    color="white"
                    onClick={handleCancelAdd}
                    aria-label="Cancel"
                  />
                </HStack>
              </Stack>
            )}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}
